/**
 * Global SiteFooter — mounts once in App.js.
 * =============================================
 *
 * Renders on public marketing + legal pages only. Two jobs:
 *   1. Entity-cluster signal to Google linking TurfPulse ↔ afincalc under
 *      the Hi Tex Lawn Pro LLC parent. Google treats two-way cross-links
 *      between same-LLC sites as a meaningful E-E-A-T trust boost.
 *   2. Consistent copyright + small-print footer so each public page doesn't
 *      have to wire its own.
 *
 * Hidden on:
 *   - /app and any protected/admin routes (logged-in experience already has
 *     its own dashboard chrome and we don't need an SEO footer there)
 *   - Kiosk routes like /console
 *   - Iframe-style routes (/api/... — handled server-side anyway)
 *
 * Pages that currently render HiTexProductsFooter inline (HomePage, HeyPage,
 * ProLandingPage) will still show it; we keep those intact for now and
 * DEDUPE them by matching their container structure. If a page owns its own
 * footer, it passes through unaffected.
 */
import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { HiTexProductsFooter } from './HiTexProductsFooter';

// Routes where the global footer should render. Anything outside this
// allow-list gets no footer (protected app routes, admin, kiosk, etc).
const PUBLIC_PATHS = new Set([
  '/',
  '/about',
  '/about/the-anthem',
  '/about/the-songbook',
  '/songbook',
  '/kit',
  '/water-budget',
  '/catch-cup',
  '/byos',
  '/fungicide-cheat-sheet',
  '/fall-guide',
  '/try',
  '/try-diagnosis',
  '/rachio/connect',
  '/rachio/rebate-helper',
  '/rachio/install-checklist',
  '/privacy',
  '/terms',
  '/accessibility',
  '/disclosure',
  '/marketplace-terms',
  '/blog',
  '/pro',
  '/hey',
  '/credits',
  '/data-sources',
  '/pricing',
  '/getting-started',
  '/ecowitt-to-rachio',
  '/methodology/probe-driven-irrigation',
  // SEO receipt pages — keep these in the footer so Google sees the
  // internal-link signal from every public marketing page.
  '/protect-your-probes',
  '/rachio-advanced-settings-vs-soil-probes',
  '/for-contractors',
  '/help-me-connect-ecowitt',
  '/round-rock-rebate',
  '/partners/contractor',
]);

// Pages that already render HiTexProductsFooter inline — suppress global
// footer on these to avoid doubling the strip. Keep synced as the inline
// footer is phased out of individual pages.
const INLINE_FOOTER_PATHS = new Set([
  '/',
  '/hey',
  '/pro',
]);

export const SiteFooter = () => {
  const { pathname } = useLocation();

  // Only render on known public pages
  if (!PUBLIC_PATHS.has(pathname) && !pathname.startsWith('/blog/')) {
    return null;
  }

  // Pages that already ship the inline strip — don't double it
  const showHiTex = !INLINE_FOOTER_PATHS.has(pathname);

  return (
    <footer
      className="border-t border-border/60 bg-background/60 py-6 mt-12"
      data-testid="site-footer"
    >
      <div className="max-w-5xl mx-auto px-5 space-y-3 text-center">
        {showHiTex ? <HiTexProductsFooter /> : null}
        <div className="text-[11px] text-muted-foreground/70">
          © {new Date().getFullYear()} TurfPulse · a product of{' '}
          <strong className="text-muted-foreground">Hi Tex Lawn Pro LLC</strong>
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground/70">
          <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-link-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors" data-testid="footer-link-terms">Terms</Link>
          <Link to="/disclosure" className="hover:text-foreground transition-colors" data-testid="footer-link-disclosure">Disclosure</Link>
          <Link to="/accessibility" className="hover:text-foreground transition-colors" data-testid="footer-link-accessibility">Accessibility</Link>
          <Link to="/about" className="hover:text-foreground transition-colors" data-testid="footer-link-about">About</Link>
          <Link to="/getting-started" className="hover:text-foreground transition-colors" data-testid="footer-link-getting-started">Getting Started</Link>
          <Link to="/credits" className="hover:text-foreground transition-colors" data-testid="footer-link-credits">Data Sources</Link>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
